import { useEffect, useMemo, useRef, useState } from "react";
import {
  SEARCH_AIRPORTS,
  priceStrip,
  searchFlights,
  startOfDay,
  TIME_WINDOWS,
} from "../lib/flightSearch.js";
import "./FlightSearch.css";

const DAY_MS = 86_400_000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
const fmtDate = (d) => `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;

// A month grid popup calendar. Lets you jump weeks/months ahead and pick a day.
function CalendarPopup({ value, min, onPick, onClose }) {
  const [month, setMonth] = useState(() => {
    const m = startOfDay(value);
    m.setDate(1);
    return m;
  });
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lead = first.getDay(); // blank cells before day 1
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

  const shift = (n) => {
    const m = new Date(month);
    m.setMonth(m.getMonth() + n);
    setMonth(m);
  };

  return (
    <div className="fs-cal" role="dialog" aria-label="Choose a date">
      <div className="fs-cal-head">
        <button type="button" onClick={() => shift(-1)} aria-label="Previous month">‹</button>
        <strong>{MONTHS[month.getMonth()]} {month.getFullYear()}</strong>
        <button type="button" onClick={() => shift(1)} aria-label="Next month">›</button>
      </div>
      <div className="fs-cal-grid fs-cal-dow">
        {WEEKDAYS.map((w) => <span key={w} className="fs-cal-wd">{w[0]}</span>)}
      </div>
      <div className="fs-cal-grid">
        {cells.map((d, i) =>
          d == null ? (
            <span key={`b${i}`} className="fs-cal-blank" />
          ) : (
            <button
              key={d.toISOString()}
              type="button"
              className={
                "fs-cal-day" +
                (sameDay(d, value) ? " sel" : "") +
                (min && startOfDay(d) < startOfDay(min) ? " past" : "")
              }
              disabled={min && startOfDay(d) < startOfDay(min)}
              onClick={() => { onPick(d); onClose(); }}
            >
              {d.getDate()}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export default function FlightSearch({ open, onClose }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [origin, setOrigin] = useState(SEARCH_AIRPORTS[0]?.iata || "JFK");
  const [destination, setDestination] = useState(SEARCH_AIRPORTS[1]?.iata || "LAX");
  const [date, setDate] = useState(() => new Date(today.getTime() + 21 * DAY_MS)); // 3 wks out
  const [timeWin, setTimeWin] = useState("any");
  const [calOpen, setCalOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const stripRef = useRef(null);

  const swap = () => { setOrigin(destination); setDestination(origin); };

  // Weeks-ahead price scroller: ~10 weeks of fares from today.
  const strip = useMemo(
    () => priceStrip(origin, destination, today, 70, today),
    [origin, destination, today]
  );
  const stripMin = useMemo(
    () => strip.reduce((m, d) => Math.min(m, d.price), Infinity),
    [strip]
  );

  // Fetch (estimate) flights whenever the route/date changes.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true);
    searchFlights(origin, destination, date, today).then((r) => {
      if (live) { setResults(r); setLoading(false); }
    });
    return () => { live = false; };
  }, [open, origin, destination, date, today]);

  // Keep the selected day visible in the scroller.
  useEffect(() => {
    if (!open) return;
    const el = stripRef.current?.querySelector(".fs-strip-cell.sel");
    el?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [open, date, origin, destination]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { calOpen ? setCalOpen(false) : onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, calOpen, onClose]);

  if (!open) return null;

  const win = TIME_WINDOWS.find((w) => w.id === timeWin) || TIME_WINDOWS[0];
  const shown = (results?.flights || []).filter((f) => win.test(f.depMinutes));
  const cheapestShown = shown.reduce((m, f) => Math.min(m, f.price), Infinity);

  return (
    <div className="fs-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="fs-panel" role="dialog" aria-label="Find a flight">
        <header className="fs-top">
          <h2>🎫 Find a flight</h2>
          <span className="fs-rough" title="Estimates, not bookable quotes — no live fare data">
            rough estimates
          </span>
          <button className="fs-close" onClick={onClose} aria-label="Close flight search">✕</button>
        </header>

        {/* Route + date + time controls */}
        <div className="fs-controls">
          <label className="fs-field">
            <span>From</span>
            <select value={origin} onChange={(e) => setOrigin(e.target.value)}>
              {SEARCH_AIRPORTS.map((a) => (
                <option key={a.iata} value={a.iata}>{a.iata} — {a.name}</option>
              ))}
            </select>
          </label>

          <button className="fs-swap" onClick={swap} aria-label="Swap origin and destination" title="Swap">⇄</button>

          <label className="fs-field">
            <span>To</span>
            <select value={destination} onChange={(e) => setDestination(e.target.value)}>
              {SEARCH_AIRPORTS.map((a) => (
                <option key={a.iata} value={a.iata}>{a.iata} — {a.name}</option>
              ))}
            </select>
          </label>

          <div className="fs-field fs-datefield">
            <span>Date</span>
            <button className="fs-datebtn" onClick={() => setCalOpen((o) => !o)} aria-expanded={calOpen}>
              📅 {fmtDate(date)}
            </button>
            {calOpen && (
              <CalendarPopup
                value={date}
                min={today}
                onPick={setDate}
                onClose={() => setCalOpen(false)}
              />
            )}
          </div>

          <label className="fs-field">
            <span>Departure time</span>
            <select value={timeWin} onChange={(e) => setTimeWin(e.target.value)}>
              {TIME_WINDOWS.map((w) => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Weeks-ahead price scroller */}
        <div className="fs-strip-wrap">
          <div className="fs-strip-label">
            Fares by day · scroll weeks ahead
            {origin === destination && <em> — pick two different airports</em>}
          </div>
          <div className="fs-strip" ref={stripRef}>
            {strip.map(({ date: d, price, cheapest }) => (
              <button
                key={d.toISOString()}
                className={
                  "fs-strip-cell" +
                  (sameDay(d, date) ? " sel" : "") +
                  (cheapest ? " cheap" : "")
                }
                onClick={() => setDate(d)}
                title={cheapest ? "Cheapest day in range" : ""}
              >
                {(d.getDate() === 1 || sameDay(d, strip[0]?.date)) && (
                  <span className="fs-strip-mon">{MONTHS[d.getMonth()]}</span>
                )}
                <span className="fs-strip-wd">{WEEKDAYS[d.getDay()][0]}</span>
                <span className="fs-strip-dom">{d.getDate()}</span>
                <span className="fs-strip-price">${price}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Results for the selected day */}
        <div className="fs-results">
          {loading && <p className="fs-empty">Estimating flights…</p>}
          {!loading && shown.length === 0 && (
            <p className="fs-empty">No flights match — try a different time window or date.</p>
          )}
          {!loading && shown.length > 0 && (
            <>
              <div className="fs-results-head">
                <span>{shown.length} flights · {fmtDate(date)}</span>
                {results?.distanceKm != null && <span>{results.distanceKm.toLocaleString()} km</span>}
              </div>
              <ul className="fs-list">
                {shown.map((f) => (
                  <li key={f.id} className={"fs-flight" + (f.price === cheapestShown ? " best" : "")}>
                    <div className="fs-flight-time">
                      <strong>{f.depTime}</strong>
                      <span className="fs-arrow">→</span>
                      <strong>{f.arrTime}{f.arrivesNextDay && <sup>+1</sup>}</strong>
                    </div>
                    <div className="fs-flight-mid">
                      <span className="fs-flight-carrier">{f.carrier} · {f.id}</span>
                      <span className="fs-flight-dur">
                        {f.durationLabel} · {f.stops === 0 ? "Nonstop" : `${f.stops} stop`}
                      </span>
                    </div>
                    <div className="fs-flight-price">
                      ${f.price}
                      {f.price === cheapestShown && <span className="fs-best-tag">best</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
